import * as THREE from 'three';
import type { GeodataColorScheme, GeoObjectMode, IGeoObject, RawDataGroup } from '@visualizer/types';
import { BuildingSource, PreProcessingTypes } from '@visualizer/types';
import { GeoObject } from '@data/geodata/GeoObject';
import { CustomBuildingsBatchedMesh, ProjectSketchBatchedMesh, SwisstopoBatchedMesh } from './BuildingsBatchedMesh';
import { CustomObstructionSingleMesh, ProjectSketchSingleMesh, SwisstopoSingleMesh } from '@data/geodata/building/BuildingsSingleMesh';
import { getProjectSketchBorders } from '@data/geodata/building/utils';
import type { BuildingsVisual } from '../edit/Interfaces/BuildingsVisual';
import FVector from '@data/geometry/FVector';

export interface BuildingsCompositeOptions {
    name: string;
    colorScheme: GeodataColorScheme;
    data: RawDataGroup[];
    mode: GeoObjectMode;
    individualBuildings: boolean;
}

export class BuildingsComposite extends THREE.Group implements IGeoObject, BuildingsVisual {
    buildings: Map<BuildingSource, GeoObject<any>> = new Map();
    private buildingsVisible = true;
    private readonly sourceVisibility = new Map<BuildingSource, boolean>();

    // Temp overlay (scene node)
    private readonly tempOverlayGroup = new THREE.Group();

    // Temp overlay bookkeeping
    private readonly tempBuildings = new Map<number, THREE.Object3D>();

    // Picking overlay (scene node)
    private readonly pickingOverlay = new THREE.Group();
    private readonly pickablesById = new Map<number, THREE.Object3D>();
    private readonly projectSketchFloorGroups: RawDataGroup[] = [{ type: PreProcessingTypes.PROJECT_SKETCH_FLOOR, objects: [] }];

    constructor({ name, colorScheme, data, mode, individualBuildings }: BuildingsCompositeOptions) {
        super();
        this.name = name;

        const swisstopo = [PreProcessingTypes.SWISSTOPO_ROOF, PreProcessingTypes.SWISSTOPO_WALL, PreProcessingTypes.SWISSTOPO_FLOOR];
        const customBuildings = [
            PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR,
            PreProcessingTypes.CUSTOM_BUILDINGS_ROOF,
            PreProcessingTypes.CUSTOM_BUILDINGS_WALL,
        ];
        const projectSketch = [
            PreProcessingTypes.PROJECT_SKETCH_FLOOR,
            PreProcessingTypes.PROJECT_SKETCH_ROOF,
            PreProcessingTypes.PROJECT_SKETCH_WALL,
        ];
        const swisstopoClass = individualBuildings ? SwisstopoBatchedMesh : SwisstopoSingleMesh;
        this.buildings.set(
            BuildingSource.SWISSTOPO,
            // new BuildingsBatch({ name: 'StandardBuildings', type: BuildingSource.SWISSTOPO, colorScheme, data: data.swisstopo })
            new swisstopoClass({
                name: 'StandardBuildings',
                colors: colorScheme,
                data: data.filter((d) => swisstopo.includes(d.type)),
                mode,
            })
        );
        const customData = data.filter((d) => customBuildings.includes(d.type));

        const customBuildingsClass = individualBuildings ? CustomBuildingsBatchedMesh : CustomObstructionSingleMesh;

        this.buildings.set(
            BuildingSource.CUSTOM_OBS,
            new customBuildingsClass({
                name: 'Custom Obstruction',
                colors: colorScheme,
                data: customData, // can be []
                mode,
            })
        );

        const projectSketchClass = individualBuildings ? ProjectSketchBatchedMesh : ProjectSketchSingleMesh;
        this.projectSketchFloorGroups[0].objects.push(
            ...data.filter((d) => d.type === PreProcessingTypes.PROJECT_SKETCH_FLOOR).flatMap((group) => group.objects ?? [])
        );
        this.buildings.set(
            BuildingSource.SKETCH,
            new projectSketchClass({
                name: 'Project Sketch',
                colors: colorScheme,
                data: data.filter((d) => projectSketch.includes(d.type)),
                mode,
            })
        );

        this.buildings.forEach((b: GeoObject<any>, source) => {
            this.sourceVisibility.set(source, true);
            this.add(b);
        });
        this.syncBuildingVisibility();

        this.tempOverlayGroup.name = 'BuildingsTempOverlay';
        this.add(this.tempOverlayGroup);

        this.pickingOverlay.name = 'BuildingsPickingOverlay';
        this.add(this.pickingOverlay);
    }

    export(): { name: string; mesh: THREE.Mesh }[] {
        const toReturn: { name: string; mesh: THREE.Mesh }[] = [];
        this.buildings.forEach((b: GeoObject<any>) => toReturn.push(...b.export()));
        return toReturn;
    }

    has(source: BuildingSource) {
        return this.buildings.has(source);
    }

    setVisible(value: boolean = true): void {
        this.buildingsVisible = value;
        this.syncBuildingVisibility();
    }

    setBuildingSourceVisible(source: BuildingSource, visible: boolean): void {
        const obj = this.buildings.get(source);
        if (!obj) return;
        this.sourceVisibility.set(source, visible);
        this.syncBuildingVisibility(source);
    }

    hasVisibleBuildings(source: BuildingSource): boolean {
        const obj = this.buildings.get(source);
        if (!obj) return false;

        if (obj instanceof CustomBuildingsBatchedMesh || obj instanceof ProjectSketchBatchedMesh) {
            return obj.hasVisibleBuildings();
        }

        return false;
    }

    dispose(): void {
        this.buildings.forEach((b: GeoObject<any>) => b.dispose());

        // Clear temp overlay
        for (const tempId of Array.from(this.tempBuildings.keys())) {
            this.removeTempBuilding(tempId);
        }

        for (const id of Array.from(this.pickablesById.keys())) {
            this.removePickingBuilding(id);
        }

        this.tempOverlayGroup.clear();
        this.pickingOverlay.clear();
    }

    changeColorScheme(colorScheme: GeodataColorScheme): void {
        this.buildings.forEach((b: GeoObject<any>) => b.changeColorScheme(colorScheme));
    }

    setMode(mode: GeoObjectMode): void {
        this.buildings.forEach((b: GeoObject<any>) => b.setMode(mode));
    }

    isVisible(): boolean {
        return this.buildingsVisible;
    }

    private syncBuildingVisibility(source?: BuildingSource): void {
        if (source != null) {
            this.applySourceVisibility(source);
            return;
        }

        this.buildings.forEach((_obj, currentSource) => {
            this.applySourceVisibility(currentSource);
        });
    }

    private applySourceVisibility(source: BuildingSource): void {
        const obj = this.buildings.get(source);
        if (!obj) return;

        const effectiveVisible = this.isSourceEffectivelyVisible(source);
        obj.setVisible(effectiveVisible);
    }

    private isSourceEffectivelyVisible(source: BuildingSource): boolean {
        const sourceVisible = this.sourceVisibility.get(source) ?? true;
        return source === BuildingSource.SKETCH ? sourceVisible : this.buildingsVisible && sourceVisible;
    }

    // ---------------------------------------------------------------------
    // Add Operation Logic
    // ---------------------------------------------------------------------
    addCustomBuildingToBatch(buildingId: number, groups: RawDataGroup[]) {
        const custom = this.buildings.get(BuildingSource.CUSTOM_OBS);
        console.log('[addCustomBuildingToBatch] custom class:', custom?.constructor?.name);

        if (custom instanceof CustomBuildingsBatchedMesh) {
            custom.addBuildingFromGroups(buildingId, groups);
            return true;
        }
        return false;
    }

    addProjectSketchToBatch(buildingId: number, groups: RawDataGroup[]) {
        const sketch = this.buildings.get(BuildingSource.SKETCH);

        if (sketch instanceof ProjectSketchBatchedMesh) {
            sketch.addBuildingFromGroups(buildingId, groups);
            const floorGroup = groups.find((g) => g.type === PreProcessingTypes.PROJECT_SKETCH_FLOOR);
            if (floorGroup?.objects?.length) this.projectSketchFloorGroups[0].objects.push(...floorGroup.objects);
            return true;
        }

        return false;
    }

    public getProjectSketchContourCount(): number {
        return getProjectSketchBorders(this.projectSketchFloorGroups, 0).length;
    }

    public getProjectSketchContourPoints(elevation: number, contourIndex: number = 0): FVector[] {
        const loops = getProjectSketchBorders(this.projectSketchFloorGroups, elevation);
        if (!loops.length) return [];
        const safeIndex = Math.max(0, Math.min(contourIndex, loops.length - 1));
        return loops[safeIndex] ?? [];
    }

    addPickingBuilding(id: number, obj: THREE.Object3D) {
        if (this.pickablesById.has(id)) return;
        this.pickablesById.set(id, obj);
        this.pickingOverlay.add(obj);
    }

    removePickingBuilding(id: number) {
        const obj = this.pickablesById.get(id);
        if (!obj) return;
        this.pickingOverlay.remove(obj);
        this.pickablesById.delete(id);

        obj.traverse((o) => {
            const m = o as any;
            if (m?.isMesh) {
                m.geometry?.dispose?.();
                m.material?.dispose?.();
            }
        });
    }

    addTempBuilding(tempId: number, obj: THREE.Object3D): void {
        if (this.tempBuildings.has(tempId)) return;

        obj.userData.tempBuildingId = tempId;
        this.tempBuildings.set(tempId, obj);
        this.tempOverlayGroup.add(obj);
    }

    removeTempBuilding(tempId: number): void {
        const obj = this.tempBuildings.get(tempId);
        if (!obj) return;

        this.tempOverlayGroup.remove(obj);
        this.tempBuildings.delete(tempId);

        obj.traverse((o) => {
            const m = o as any;
            if (m?.isMesh) {
                m.geometry?.dispose?.();
                const mat = m.material;
                if (mat?.dispose) mat.dispose();
            }
        });
    }

    // ---------------------------------------------------------------------
    // BuildingsVisual implementation
    // ---------------------------------------------------------------------
    hideBuilding(source: BuildingSource, id: number): void {
        const obj = this.buildings.get(source);
        if (!obj) return;

        if (obj instanceof SwisstopoBatchedMesh || obj instanceof CustomBuildingsBatchedMesh || obj instanceof ProjectSketchBatchedMesh) {
            obj.hideObject(id);
        }
    }

    showBuilding(source: BuildingSource, id: number): void {
        const obj = this.buildings.get(source);
        if (!obj) return;

        if (obj instanceof SwisstopoBatchedMesh || obj instanceof CustomBuildingsBatchedMesh || obj instanceof ProjectSketchBatchedMesh) {
            obj.showObject(id);
        }
    }

    // ---------------------------------------------------------------------
    // Extra public API for hover / highlight
    // ---------------------------------------------------------------------
    highlightBuilding(source: BuildingSource, id: number): void {
        const obj = this.buildings.get(source);
        if (!obj) return;

        if (obj instanceof SwisstopoBatchedMesh || obj instanceof CustomBuildingsBatchedMesh || obj instanceof ProjectSketchBatchedMesh) {
            obj.highlightObject(id);
        }
    }

    clearHighlight(): void {
        this.buildings.forEach((obj) => {
            if (
                obj instanceof SwisstopoBatchedMesh ||
                obj instanceof CustomBuildingsBatchedMesh ||
                obj instanceof ProjectSketchBatchedMesh
            ) {
                obj.clearHighlight();
            }
        });
    }

    // ---------------------------------------------------------------------
    // Extra public API for rebuilding merged edges
    // ---------------------------------------------------------------------
    rebuildMergedEdges(): void {
        this.buildings.forEach((obj) => {
            if (
                obj instanceof SwisstopoBatchedMesh ||
                obj instanceof CustomBuildingsBatchedMesh ||
                obj instanceof ProjectSketchBatchedMesh
            ) {
                obj.rebuildMergedEdges();
            }
        });
    }

    // ---------------------------------------------------------------------
    // Raw data extraction (for edit operations)
    // ---------------------------------------------------------------------
    getRawGroupsForBuilding(
        source: BuildingSource,
        buildingId: number,
        target: { source: BuildingSource; id: number }
    ): RawDataGroup[] | null {
        const obj = this.buildings.get(source);
        if (!obj) return null;

        if (target.source !== BuildingSource.SKETCH) {
            return null;
        }

        const mapTypeToSketch = (type: PreProcessingTypes): PreProcessingTypes => {
            switch (type) {
                case PreProcessingTypes.SWISSTOPO_ROOF:
                case PreProcessingTypes.CUSTOM_BUILDINGS_ROOF:
                case PreProcessingTypes.PROJECT_SKETCH_ROOF:
                    return PreProcessingTypes.PROJECT_SKETCH_ROOF;
                case PreProcessingTypes.SWISSTOPO_WALL:
                case PreProcessingTypes.CUSTOM_BUILDINGS_WALL:
                case PreProcessingTypes.PROJECT_SKETCH_WALL:
                    return PreProcessingTypes.PROJECT_SKETCH_WALL;
                case PreProcessingTypes.SWISSTOPO_FLOOR:
                case PreProcessingTypes.CUSTOM_BUILDINGS_FLOOR:
                case PreProcessingTypes.PROJECT_SKETCH_FLOOR:
                    return PreProcessingTypes.PROJECT_SKETCH_FLOOR;
                default:
                    return type;
            }
        };

        if (obj instanceof SwisstopoBatchedMesh || obj instanceof CustomBuildingsBatchedMesh || obj instanceof ProjectSketchBatchedMesh) {
            return obj.getRawGroupsForBuilding(buildingId, target.id, target.source, mapTypeToSketch);
        }

        return null;
    }
}
